import type React from "react";
import { useRef, useState } from "react";

import { Box, Stack, TextField, styled } from "@mui/material";
import clsx from "clsx";
import { Controller, FormProvider, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useMessagesSearchParams } from "routes/messages/hooks/useMessagesSearchParams";
import { BaseButton } from "shared/atoms/BaseButton";
import { EmojiPicker } from "shared/atoms/EmojiPicker";
import { QuickReplies } from "shared/atoms/inputs";
import type { QuickRepliesSize } from "shared/atoms/inputs/QuickReplies/quickRepliesConfig";
import { useWindowSize } from "utils/hooks";

interface MessageInputProps {
  disabled?: boolean;
  error?: boolean;
  form: UseFormReturn<{ body: string }>;
  helperText?: React.ReactNode;
  placeholder?: string;
  quickRepliesSize?: QuickRepliesSize;
  slots?: {
    calendar?: boolean;
    emoji?: boolean;
    quickReplies?: boolean;
    quickReplyShortcuts?: boolean;
  };
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  disabled = false,
  error,
  form,
  helperText,
  placeholder,
  quickRepliesSize = "default",
  slots: { calendar = true, emoji = true, quickReplies = true, quickReplyShortcuts = true } = {},
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { isBookingFormOpen, togglePanel } = useMessagesSearchParams();
  const { isLargeScreen } = useWindowSize();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>();

  const handleContentSelect = (content: string) => {
    const textArea = textAreaRef.current;
    if (textArea) {
      const cursorPosition = textArea.selectionStart;
      const currentValue = form.getValues().body;
      const textBeforeCursor = currentValue.slice(0, cursorPosition);
      const textAfterCursor = currentValue.slice(cursorPosition);
      const newText = textBeforeCursor + content + textAfterCursor;
      form.setValue("body", newText, { shouldValidate: true });
    }
    setShowEmojiPicker(false);
  };

  return (
    <Box sx={{ borderTop: "1px solid #E8EFF7" }}>
      <FormProvider {...form}>
        <form onSubmit={onSubmit} data-testid="message-form" noValidate>
          <Controller
            name="body"
            control={form.control}
            render={({ field }) => (
              <StyledTextField
                {...field}
                autoFocus
                disabled={disabled}
                inputRef={textAreaRef}
                placeholder={placeholder || t("messages.input_placeholder")}
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                error={error}
                helperText={helperText}
              />
            )}
          />

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} padding={1}>
              {calendar && (
                <BaseButton
                  data-testid="schedule-form-toggle"
                  disabled={disabled}
                  variant="action"
                  icon="calendar"
                  onClick={() => togglePanel("book")}
                  className={clsx({ active: isBookingFormOpen })}
                  style={{ maxHeight: "40px", padding: "12px 16px" }}
                />
              )}
              {emoji && isLargeScreen && (
                <Box sx={{ display: "flex", alignItems: "stretch", position: "relative" }}>
                  <BaseButton
                    disabled={disabled}
                    variant="action"
                    icon="smile"
                    onClick={event => {
                      event.stopPropagation();
                      setShowEmojiPicker(prevState => !prevState);
                    }}
                    className={clsx({ active: showEmojiPicker })}
                    style={{ maxHeight: "40px", padding: "12px 16px" }}
                  />
                  {showEmojiPicker && (
                    <Box sx={{ position: "absolute", bottom: "60px" }}>
                      <EmojiPicker
                        onClickOutside={() => setShowEmojiPicker(false)}
                        onEmojiSelect={handleContentSelect}
                      />
                    </Box>
                  )}
                </Box>
              )}
              {quickReplies && (
                <QuickReplies
                  disabled={disabled}
                  onQuickReplySelected={handleContentSelect}
                  showShortcuts={quickReplyShortcuts}
                  quickRepliesSize={quickRepliesSize}
                />
              )}

              <BaseButton
                disabled={disabled || form.watch("body").trim() === ""}
                type="submit"
                variant="primary"
                icon="paper-plane"
                style={{
                  flexBasis: "auto",
                  flexGrow: 0,
                  marginLeft: "auto",
                  minHeight: "auto",
                  maxHeight: "40px",
                  padding: "12px 24px",
                }}
              />
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Box>
  );
};

const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 0,

    "& fieldset": {
      border: "none",
    },
  },
});
